import debounce from "lodash/debounce";

import type {
  CommentCountsApiResponse,
  CommentCountsResponse,
} from "@framework/comments/comments.types";
import {
  commentCountStore,
  type CommentCountUpdate,
} from "@framework/coral/CommentCountStore";

import { CoralIndexedDBUtils } from "./coral-indexeddb";

const CORAL_COUNTS_API_PATH = "/api/coral-counts";

class CoralService {
  private pendingCoralIds: string[] = [];
  private registeredCoralIds = new Set<string>();
  private debouncedFetch: () => void;

  constructor() {
    this.debouncedFetch = debounce(
      () => {
        void this.fetchCommentCounts();
      },
      100,
      { maxWait: 1000 },
    );
  }

  registerCountId(coralId: string): void {
    if (this.registeredCoralIds.has(coralId)) {
      return;
    }

    this.registeredCoralIds.add(coralId);
    this.pendingCoralIds.push(coralId);
    this.debouncedFetch();
  }

  private async fetchCommentCounts(): Promise<void> {
    const coralIds = [...this.pendingCoralIds];
    this.pendingCoralIds = [];

    if (coralIds.length === 0) {
      return;
    }

    try {
      const params = new URLSearchParams();
      coralIds.sort().forEach((coralId) => params.append("c", coralId));
      const requestUrl = `${CORAL_COUNTS_API_PATH}?${params.toString()}`;

      const response = await fetch(requestUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData = (await response.json()) as CommentCountsApiResponse;
      const coralCounts: CommentCountsResponse = responseData.data;

      const lastModifiedHeader = response.headers.get("Last-Modified");
      const dataTimestamp = lastModifiedHeader
        ? new Date(lastModifiedHeader).getTime()
        : Date.now();

      const updates: CommentCountUpdate[] = [];

      for (const coralId of coralIds) {
        const count = coralCounts[coralId];
        if (typeof count !== "number") {
          continue;
        }

        updates.push({
          countKey: coralId,
          count,
          timestamp: dataTimestamp,
        });
      }

      if (updates.length > 0) {
        await commentCountStore.updateCounts(updates);
      }
    } catch (error) {
      console.error("Failed to fetch comment counts:", error);
    }
  }

  async updateCommentCount(
    coralId: string,
    count: number,
    timestamp: number,
  ): Promise<void> {
    await commentCountStore.updateCommentCount({
      countKey: coralId,
      count,
      timestamp,
    });
  }

  getCommentCount(coralId: string): number | null {
    return commentCountStore.getCommentCount(coralId);
  }

  subscribeToCommentCount(
    coralId: string,
    callback: (count: number) => void,
  ): () => void {
    return commentCountStore.subscribe(coralId, callback);
  }

  async loadAllCountsFromIndexedDB(): Promise<void> {
    try {
      const coralDb = await CoralIndexedDBUtils.init();
      const updates = await coralDb.loadAllCommentCounts();

      if (updates.length > 0) {
        await commentCountStore.updateCounts(updates);
      }
    } catch (error) {
      console.warn("IndexedDB not available, skipping count loading:", error);
    }
  }

  async getLastReadCount(coralId: string): Promise<number | null> {
    try {
      const coralDb = await CoralIndexedDBUtils.init();
      return await coralDb.getLastReadCount(coralId);
    } catch (error) {
      console.warn("IndexedDB failed, falling back to localStorage:", error);
      return this.getLastReadCountFromLocalStorage(coralId);
    }
  }

  private getLastReadCountFromLocalStorage(coralId: string): number | null {
    const stored = localStorage.getItem(`comment-count-${coralId}`);
    if (stored) {
      return parseInt(stored, 10);
    }
    return null;
  }

  async setLastReadCount(coralId: string, count: number): Promise<void> {
    try {
      const coralDb = await CoralIndexedDBUtils.init();
      await coralDb.setLastReadCount(coralId, count);
    } catch (error) {
      console.warn("IndexedDB failed, falling back to localStorage:", error);
      localStorage.setItem(`comment-count-${coralId}`, count.toString());
    }
  }
}

let coralServiceInstance: CoralService | null = null;

export const coralService = {
  getCommentCount: (coralId: string) =>
    getCoralService().getCommentCount(coralId),
  updateCommentCount: (coralId: string, count: number, timestamp: number) =>
    getCoralService().updateCommentCount(coralId, count, timestamp),
  subscribeToCommentCount: (
    coralId: string,
    callback: (count: number) => void,
  ) => getCoralService().subscribeToCommentCount(coralId, callback),
  registerCountId: (coralId: string) =>
    getCoralService().registerCountId(coralId),
  loadAllCountsFromIndexedDB: () =>
    getCoralService().loadAllCountsFromIndexedDB(),
  getLastReadCount: (coralId: string) =>
    getCoralService().getLastReadCount(coralId),
  setLastReadCount: (coralId: string, count: number) =>
    getCoralService().setLastReadCount(coralId, count),
};

function getCoralService(): CoralService {
  if (!coralServiceInstance) {
    coralServiceInstance = new CoralService();
  }
  return coralServiceInstance;
}
